import "@/App.css";
import { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster, toast } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { KeyRound } from "lucide-react";
import axios from "axios";
import Layout from "@/components/Layout";
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import RegisterCompany from "@/pages/RegisterCompany";
import Dashboard from "@/pages/Dashboard";
import Einsatzplanung from "@/pages/Einsatzplanung";
import Projekte from "@/pages/Projekte";
import Mitarbeiter from "@/pages/Mitarbeiter";
import Urlaubsverwaltung from "@/pages/Urlaubsverwaltung";
import Dokumente from "@/pages/Dokumente";
import Finanzen from "@/pages/Finanzen";
import Kunden from "@/pages/Kunden";
import Lagerverwaltung from "@/pages/Lagerverwaltung";
import Materialliste from "@/pages/Materialliste";
import Rechnungsposten from "@/pages/Rechnungsposten";
import Einstellungen from "@/pages/Einstellungen";
import SuperAdmin from "@/pages/SuperAdmin";
import KalenderUebersicht from "@/pages/KalenderUebersicht";

function ProtectedRoute({ children }) {
  const { user, loading, API } = useAuth();
  const [showPwChange, setShowPwChange] = useState(user?.must_change_password);
  const [pw, setPw] = useState({ current: '', new1: '', new2: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user?.must_change_password) setShowPwChange(true);
  }, [user]);

  if (loading) return (
    <div className="flex items-center justify-center h-screen bg-slate-950">
      <div className="text-amber-500 font-medium animate-pulse">Laden...</div>
    </div>
  );
  if (!user) return <Navigate to="/login" />;

  const handleChangePw = async () => {
    if (pw.new1 !== pw.new2) { toast.error('Passwörter stimmen nicht überein'); return; }
    if (pw.new1.length < 6) { toast.error('Mind. 6 Zeichen'); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/auth/change-password`, { current_password: pw.current, new_password: pw.new1 });
      setShowPwChange(false);
      toast.success('Passwort geändert');
      window.location.reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
    finally { setSaving(false); }
  };

  return (
    <Layout>
      {children}
      {showPwChange && (
        <div className="fixed inset-0 z-[100] bg-black/70 flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-slate-800 rounded-sm p-6 max-w-sm w-full space-y-4">
            <div className="text-center">
              <div className="w-12 h-12 bg-amber-500/10 rounded-full flex items-center justify-center mx-auto mb-3">
                <KeyRound className="w-6 h-6 text-amber-400" />
              </div>
              <h2 className="text-lg font-bold text-white">Passwort ändern</h2>
              <p className="text-sm text-slate-400 mt-1">Bitte ändern Sie Ihr Erstpasswort bevor Sie fortfahren.</p>
            </div>
            <div>
              <Label className="text-slate-300 text-sm">Aktuelles Passwort (aus E-Mail)</Label>
              <Input type="password" value={pw.current} onChange={e => setPw({...pw, current: e.target.value})}
                placeholder="Passwort aus der Willkommens-E-Mail" className="bg-slate-950 border-slate-800 text-slate-200 placeholder:text-slate-600 mt-1" />
            </div>
            <div>
              <Label className="text-slate-300 text-sm">Neues Passwort</Label>
              <Input type="password" value={pw.new1} onChange={e => setPw({...pw, new1: e.target.value})}
                placeholder="Mind. 6 Zeichen" className="bg-slate-950 border-slate-800 text-slate-200 placeholder:text-slate-600 mt-1" />
            </div>
            <div>
              <Label className="text-slate-300 text-sm">Passwort bestätigen</Label>
              <Input type="password" value={pw.new2} onChange={e => setPw({...pw, new2: e.target.value})}
                className="bg-slate-950 border-slate-800 text-slate-200 mt-1" />
            </div>
            <Button onClick={handleChangePw} disabled={saving} className="w-full bg-amber-500 text-slate-950 hover:bg-amber-400 font-bold rounded-sm">
              {saving ? 'Speichern...' : 'Passwort ändern & fortfahren'}
            </Button>
          </div>
        </div>
      )}
    </Layout>
  );
}

function AppRoutes() {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="flex items-center justify-center h-screen bg-slate-950">
      <div className="text-amber-500 font-medium animate-pulse">Laden...</div>
    </div>
  );

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={user ? <Navigate to="/dashboard" /> : <LoginPage />} />
      <Route path="/register" element={user ? <Navigate to="/dashboard" /> : <RegisterCompany />} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/einsatzplanung" element={<ProtectedRoute><Einsatzplanung /></ProtectedRoute>} />
      <Route path="/kalender" element={<ProtectedRoute><KalenderUebersicht /></ProtectedRoute>} />
      <Route path="/projekte" element={<ProtectedRoute><Projekte /></ProtectedRoute>} />
      <Route path="/mitarbeiter" element={<ProtectedRoute><Mitarbeiter /></ProtectedRoute>} />
      <Route path="/urlaub" element={<ProtectedRoute><Urlaubsverwaltung /></ProtectedRoute>} />
      <Route path="/dokumente" element={<ProtectedRoute><Dokumente /></ProtectedRoute>} />
      <Route path="/finanzen" element={<ProtectedRoute><Finanzen /></ProtectedRoute>} />
      <Route path="/rechnungsposten" element={<ProtectedRoute><Rechnungsposten /></ProtectedRoute>} />
      <Route path="/lager" element={<ProtectedRoute><Lagerverwaltung /></ProtectedRoute>} />
      <Route path="/materialien" element={<ProtectedRoute><Materialliste /></ProtectedRoute>} />
      <Route path="/kunden" element={<ProtectedRoute><Kunden /></ProtectedRoute>} />
      <Route path="/einstellungen" element={<ProtectedRoute><Einstellungen /></ProtectedRoute>} />
      <Route path="/superadmin" element={<ProtectedRoute><SuperAdmin /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster theme="dark" position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
